"use client";
import getOutstandingCourses from "./getOutstandingCourses";

import { useAuth } from "@/components/AuthProvider";
import { LMS_DOMAIN } from "@/lib/course/utils/constants";
import getSkuQueryStringForPartialBundle from "./getSkuQueryStringForPartialBundle";
import getSkuQueryStringForFullBundle from "./getSkuQueryStringForFullBundle";
import { Program } from "@/common/program/types";
import { ProcessedCourse } from "@/common/course/types";
import { Enrollment } from "@/lib/partner-api/getEnrollments";
import { Entitlement } from "@/lib/partner-api/getEntitlements";
import { CommercePath, useBuildCommerceUrl } from "@/hooks/useBuildCommerceUrl";
import {
  convertStringQueryParamsToQueryParams,
  QueryParams,
} from "@/utils/functions/buildSafeUrl";
import { useLogistrationModal } from "@/components/LogistrationModal/hooks/useLogistrationModal";
import useMarketplaceAttributionHref from "@/hooks/useMarketplaceAttributionHref";

export default function useEnrollmentUrl(
  programData: Program,
  courses: ProcessedCourse[],
  skus: string[],
  enrollments: Enrollment[],
  entitlements: Entitlement[],
  emailOptIn: boolean = false,
) {
  const { applicableSeatTypes: applicableSeatTypeData, leadFormUrl } =
    programData;

  const { shouldShowModal } = useLogistrationModal();
  const { isLoggedIn } = useAuth();

  // leadFormUrl may contain legacy basket/add URLs from Partner API - we convert to commerce-coordinator format
  // Extract query params (like bundle) from the legacy URL if present
  let url: URL;
  try {
    url = new URL(leadFormUrl);
  } catch {
    // If leadFormUrl is not a valid URL, create a dummy URL to extract bundle from query params
    // This handles edge cases where leadFormUrl might be malformed
    url = new URL(leadFormUrl, "https://www.edx.org");
  }

  // remove any existing skus
  url.searchParams.delete("sku");

  const applicableSeatTypes =
    applicableSeatTypeData?.map((seatType) => seatType.slug) || [];

  const coursesInfo =
    enrollments.length + entitlements.length === 0
      ? {
          courses,
          hasSkus: false,
          hasEnrollment: false,
        }
      : getOutstandingCourses(
          courses,
          enrollments,
          entitlements,
          applicableSeatTypes,
        );

  const skuQueryString =
    coursesInfo.hasEnrollment || coursesInfo.hasSkus
      ? getSkuQueryStringForPartialBundle(
          coursesInfo.courses,
          applicableSeatTypes,
        )
      : getSkuQueryStringForFullBundle(skus);

  // Build commerce-coordinator URL, converting any legacy basket/add URLs from Partner API
  // Example: "https://commerce-coordinator.edx.org/lms/payment_page_redirect/?bundle=1859f52c-3fc0-43fe-a8a1-13660c77e9c3&sku=FD8CE26&sku=273F1D4&sku=6706D1F"
  const coordinatorUrl = useBuildCommerceUrl(
    CommercePath.PaymentRedirect,
    {
      ...(Object.fromEntries(url.searchParams.entries()) as QueryParams),
      ...convertStringQueryParamsToQueryParams(skuQueryString),
      email_opt_in: emailOptIn ? "true" : "false",
    },
    "login",
    shouldShowModal,
  );

  const userFullyEnrolled = isLoggedIn && coursesInfo.courses.length === 0;
  const enrollmentUrl = userFullyEnrolled
    ? `${LMS_DOMAIN}/dashboard/programs/`
    : coordinatorUrl;

  return useMarketplaceAttributionHref(enrollmentUrl, {
    includeNextDestination: true,
  });
}
